import { useState, useEffect, useCallback } from 'react'
import { usePageTitle } from '@components/layout/MainLayout'
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Chip,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material'
import {
  Search,
  Refresh,
  Add,
  Edit,
  Delete,
  CardGiftcard,
  FileUpload,
} from '@mui/icons-material'
import { themesApi } from '@api/endpoints/themes'
import { AdminThemeDto, CreateThemeRequest } from '@api/types/themes'
import { ThemeFormDialog, ThemeDeleteDialog, ThemeAwardDialog, ThemeImportDialog } from '../components/themes'

export default function AdminThemesPage() {
  usePageTitle('Themes')
  const [themes, setThemes] = useState<AdminThemeDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')

  // Form dialog
  const [formOpen, setFormOpen] = useState(false)
  const [editingTheme, setEditingTheme] = useState<AdminThemeDto | null>(null)
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  // Delete dialog
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deletingTheme, setDeletingTheme] = useState<AdminThemeDto | null>(null)
  const [deleting, setDeleting] = useState(false)

  // Award dialog
  const [awardOpen, setAwardOpen] = useState(false)
  const [awardTheme, setAwardTheme] = useState<AdminThemeDto | null>(null)

  // Import dialog
  const [importOpen, setImportOpen] = useState(false)

  const fetchThemes = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await themesApi.getThemes()
      setThemes(Array.isArray(data) ? data : [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch themes')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchThemes()
  }, [fetchThemes])

  const filteredThemes = themes.filter((t) => {
    if (!search) return true
    const q = search.toLowerCase()
    return t.name.toLowerCase().includes(q) || t.slug.toLowerCase().includes(q)
  })

  const handleCreate = () => {
    setEditingTheme(null)
    setFormError(null)
    setFormOpen(true)
  }

  const handleEdit = (theme: AdminThemeDto) => {
    setEditingTheme(theme)
    setFormError(null)
    setFormOpen(true)
  }

  const handleSave = async (data: CreateThemeRequest) => {
    setSaving(true)
    setFormError(null)
    try {
      if (editingTheme) {
        await themesApi.updateTheme(editingTheme.id, data)
      } else {
        await themesApi.createTheme(data)
      }
      setFormOpen(false)
      fetchThemes()
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to save theme')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteClick = (theme: AdminThemeDto) => {
    setDeletingTheme(theme)
    setDeleteOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!deletingTheme) return
    setDeleting(true)
    setError(null)
    try {
      await themesApi.deleteTheme(deletingTheme.id)
      setDeleteOpen(false)
      setDeletingTheme(null)
      fetchThemes()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete theme')
      setDeleteOpen(false)
    } finally {
      setDeleting(false)
    }
  }

  const handleAwardClick = (theme: AdminThemeDto) => {
    setAwardTheme(theme)
    setAwardOpen(true)
  }

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'N/A'
    const date = new Date(dateStr)
    return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString()
  }

  return (
    <Box>
      {/* Header */}
      <Paper sx={{ mb: 3, p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search by name or slug..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            sx={{ minWidth: 300, flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchThemes}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={<FileUpload />}
            onClick={() => setImportOpen(true)}
          >
            Import
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreate}
          >
            Create Theme
          </Button>
        </Box>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'primary.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Preview</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Name</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Slug</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Default</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Sort Order</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Users</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Created</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : filteredThemes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No themes found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredThemes.map((theme) => (
                  <TableRow key={theme.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            bgcolor: theme.previewPrimary,
                            border: '1px solid',
                            borderColor: 'divider',
                          }}
                        />
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            bgcolor: theme.previewSecondary,
                            border: '1px solid',
                            borderColor: 'divider',
                          }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">{theme.name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{theme.slug}</Typography>
                    </TableCell>
                    <TableCell>
                      {theme.isDefault && <Chip label="Default" size="small" color="primary" />}
                    </TableCell>
                    <TableCell>{theme.sortOrder}</TableCell>
                    <TableCell>{theme.userCount}</TableCell>
                    <TableCell>{formatDate(theme.createdAt)}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleEdit(theme)}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Award / Revoke">
                          <IconButton size="small" onClick={() => handleAwardClick(theme)}>
                            <CardGiftcard fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => handleDeleteClick(theme)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Dialogs */}
      <ThemeFormDialog
        open={formOpen}
        theme={editingTheme}
        saving={saving}
        error={formError}
        onClose={() => setFormOpen(false)}
        onSave={handleSave}
      />

      <ThemeDeleteDialog
        open={deleteOpen}
        theme={deletingTheme}
        deleting={deleting}
        onClose={() => { setDeleteOpen(false); setDeletingTheme(null) }}
        onConfirm={handleDeleteConfirm}
      />

      <ThemeAwardDialog
        open={awardOpen}
        theme={awardTheme}
        onClose={() => { setAwardOpen(false); setAwardTheme(null) }}
        onComplete={fetchThemes}
      />

      <ThemeImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onComplete={fetchThemes}
      />
    </Box>
  )
}
